import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { verifyEmail } from "../../../services/apiV2";
import {
  RESEND_VERIFICATION_EMAIL_FAILED,
  RESEND_VERIFICATION_EMAIL_SUCCEEDED,
} from "../../../constants";
import { wait } from "../../../helpers";
import { useResendVerificationEmail } from "../../../hooks/useResendVerificationEmail";
import { enqueueSnackbar } from "notistack";
import { toggleOnboarding } from "../../../features/onboarding/onboarding.slice";
import { useAppDispatch } from "../../../store/hooks";
import { setAmazonAccount, setTokens } from "../../../features/user/user.slice";
import { syncExtensionAuth } from "../../../utils/extension";
import { useAnalytics } from "../../../hooks/useAnalytics";

type TVerificationStatus = "idle" | "error" | "emailAlreadyVerified";

type TErrors = {
  resendVerificationEmailFailed: string | null;
};

const ERRORS_INITIAL_STATE: TErrors = {
  resendVerificationEmailFailed: null,
};

type TRegisteredFrom = "extension" | "website";

export const useEmailVerificationPageFacade = () => {
  const [status, setStatus] = useState<TVerificationStatus>("idle");
  const [errors, setErrors] = useState<TErrors>(ERRORS_INITIAL_STATE);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { trackSignupComplete } = useAnalytics();

  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const registeredFrom = searchParams.get("registeredfrom") as TRegisteredFrom;

  const { resend: resendEmail, isLoading: resendingEmail } =
    useResendVerificationEmail({
      onSuccess: () => {
        setErrors((prev) => ({
          ...prev,
          resendVerificationEmailFailed: null,
        }));
        enqueueSnackbar(RESEND_VERIFICATION_EMAIL_SUCCEEDED, {
          variant: "success",
        });
      },
      onError: () => {
        setErrors((prev) => ({
          ...prev,
          resendVerificationEmailFailed: RESEND_VERIFICATION_EMAIL_FAILED,
        }));
      },
    });

  const navigateToHome = () => {
    navigate("/");
  };

  const verify = useCallback(async () => {
    try {
      const res = await verifyEmail({
        emailConfirmedToken: token!, // Checked on UI layer
      });

      await wait(2000); // Simulate a delay for better UX

      if (res.data.verificationStatus === "Verified") {
        const { userInfo } = res.data.data;
        trackSignupComplete({
          method: "email",
          userId: userInfo.id,
          name: userInfo.fullName,
          email: userInfo.email,
          registeredFrom,
        });
        dispatch(setTokens(res.data.data));
        syncExtensionAuth(false);
        navigate(
          `/choose-plan${registeredFrom ? `?registeredFrom=${registeredFrom}` : ""}`,
          { replace: true },
        );
        dispatch(
          setAmazonAccount({
            isConnected: false,
            isValid: false,
            shouldShowModal: false,
          }),
        );
      } else if (res.data.verificationStatus === "AlreadyVerified") {
        setStatus("emailAlreadyVerified");
      } else {
        setStatus("error");
      }
    } catch (e) {
      setStatus("error");
    }
  }, [token, navigate, dispatch, registeredFrom, trackSignupComplete]);

  useEffect(() => {
    void verify();
  }, [verify]);

  return {
    token,
    email,
    status,
    errors,
    resendingEmail,
    resendEmail,
    navigateToHome,
  };
};
